import React, { useState, useMemo } from 'react';
import { View, Text, ScrollView, TouchableOpacity, StyleSheet, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { colors, typography } from '../../theme';
import { designTokens } from '../../theme/design-system';
import { OmniChannelStats, FlowSessionsResponse, AssignmentStats, DashboardCampaign, BusinessUser, WhatsAppCustomTemplate } from '../../services/api';
import { KpiTile } from './KpiTile';
import { AreaChartCard } from './AreaChartCard';
import { DonutChart } from './DonutChart';
import { FlowSessionsCard } from './FlowSessionsCard';
import { AssignmentBreakdownCard } from './AssignmentBreakdownCard';
import { UsersTeamCard } from './UsersTeamCard';
import { TemplateMessagesCard } from './TemplateMessagesCard';
import { PulsingAccentBar } from '../../components/PulsingAccentBar';
import { useThemedStyles } from '../../hooks/useThemedStyles';

/** Thin gold accent bar + title, used consistently above every section so
 * the dashboard reads as distinct zones instead of one flat scroll. When
 * `pulsing` is true, the accent bar pulses to draw the eye to sections with
 * actionable pending items (ongoing flows, overdue assignments). */
const SectionHeader: React.FC<{ title: string; pulsing?: boolean }> = ({ title, pulsing }) => {
  const styles = useThemedStyles(getStyles);
  return (
  <View style={styles.sectionHeader}>
    <PulsingAccentBar pulsing={pulsing} style={styles.sectionAccentBar} />
    <Text style={styles.sectionTitle}>{title}</Text>
  </View>
);
};

/** Promotes the single most important number (messages sent) into a hero
 * card — larger numeral, subtle gradient wash, elevated shadow — so the KPI
 * row has a visible focal point instead of four equal-weight boxes. */
const HeroStatCard: React.FC<{ value: string | number }> = ({ value }) => {
  const styles = useThemedStyles(getStyles);
  return (
  <View style={styles.heroCard}>
    <LinearGradient
      colors={colors.primaryGradient as [string, string]}
      start={{ x: 0, y: 0 }}
      end={{ x: 1, y: 1 }}
      style={styles.heroGradient}
    />
    <Ionicons name="send" size={22} color={colors.brand.primary as string} />
    <Text style={styles.heroValue} numberOfLines={1}>{value}</Text>
    <Text style={styles.heroLabel}>Messages sent</Text>
  </View>
);
};

// Same status→color mapping HomeScreen.tsx already uses for campaign pills —
// reused verbatim so a business sees the same visual language for "campaign
// status" whether they're on the old Home screen or this new dashboard.
const CAMPAIGN_STATUS_CONFIG: Record<string, { label: string; color: string; icon: string }> = {
  failed: { label: 'Failed', color: '#EF4444', icon: 'close-circle' },
  pending: { label: 'Pending', color: '#F59E0B', icon: 'time-outline' },
  sent: { label: 'Sent', color: '#3B82F6', icon: 'checkmark-outline' },
  delivered: { label: 'Delivered', color: '#8B5CF6', icon: 'checkmark-done' },
  read: { label: 'Read', color: '#10B981', icon: 'eye-outline' },
  replied: { label: 'Replied', color: '#06B6D4', icon: 'chatbubble-outline' },
};

type ChannelFilter = 'all' | 'whatsapp' | 'facebookMessenger' | 'xTwitter';
const CHANNEL_FILTERS: { id: ChannelFilter; label: string; icon: string }[] = [
  { id: 'all', label: 'All channels', icon: 'apps-outline' },
  { id: 'whatsapp', label: 'WhatsApp', icon: 'logo-whatsapp' },
  { id: 'facebookMessenger', label: 'Facebook', icon: 'logo-facebook' },
  { id: 'xTwitter', label: 'X', icon: 'logo-twitter' },
];

export interface DashboardContentProps {
  columns: number;
  loading: boolean;
  error: string | null;
  stats: OmniChannelStats | null;
  flowSessions: FlowSessionsResponse | null;
  assignmentStats: AssignmentStats | null;
  campaigns: DashboardCampaign[];
  users: BusinessUser[];
  templates: WhatsAppCustomTemplate[];
  onTeamViewAll?: () => void;
  onTeamSettings?: () => void;
  onTemplatesViewAll?: () => void;
  onTemplatesSettings?: () => void;
  onFlowsViewAll?: () => void;
  onAssignmentsViewAll?: () => void;
}

export const DashboardContent: React.FC<DashboardContentProps> = ({
  columns,
  loading,
  error,
  stats,
  flowSessions,
  assignmentStats,
  campaigns,
  users,
  templates,
  onTeamViewAll,
  onTeamSettings,
  onTemplatesViewAll,
  onTemplatesSettings,
  onFlowsViewAll,
  onAssignmentsViewAll,
}) => {
  const styles = useThemedStyles(getStyles);
  const [channelFilter, setChannelFilter] = useState<ChannelFilter>('all');

  const kpiTileWidth = columns >= 4 ? 200 : undefined;

  const channelDonutSegments = useMemo(() => {
    if (!stats) return [];
    return [
      { label: 'WhatsApp', value: stats.channels.whatsapp.active, color: '#25D366' },
      { label: 'Facebook', value: stats.channels.facebookMessenger.active, color: '#1877F2' },
      { label: 'X', value: stats.channels.xTwitter.active, color: colors.text.primary as string },
    ];
  }, [stats]);

  const filteredChannelStat = useMemo(() => {
    if (!stats || channelFilter === 'all') return null;
    return stats.channels[channelFilter];
  }, [stats, channelFilter]);

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.brand.primary as string} />
      </View>
    );
  }

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      {!!error && (
        <View style={styles.errorBanner}>
          <Ionicons name="alert-circle-outline" size={16} color={colors.status.error as string} />
          <Text style={styles.errorText}>{error}</Text>
        </View>
      )}

      <SectionHeader title="Overview" />
      <View style={styles.kpiRow}>
        <HeroStatCard value={stats?.messages.total ?? '—'} />
        <KpiTile index={1} minWidth={kpiTileWidth} icon="chatbubbles-outline" label="Conversations" value={stats?.conversations.total ?? '—'} />
        <KpiTile index={2} minWidth={kpiTileWidth} icon="people-outline" label="Contacts" value={stats?.contacts.total ?? '—'} />
        <KpiTile index={3} minWidth={kpiTileWidth} icon="apps-outline" label="Active channels" value={stats?.channels.active ?? '—'} />
      </View>

      <View style={[styles.row, columns < 2 && styles.rowStacked]}>
        <View style={styles.chartCard}>
          <AreaChartCard title="Message volume" data={stats?.messages.dailyVolume ?? []} />
        </View>
        <View style={styles.chartCard}>
          <Text style={styles.cardTitle}>Channel breakdown</Text>
          <DonutChart segments={channelDonutSegments} centerLabel={String(stats?.channels.active ?? 0)} />
        </View>
      </View>

      <SectionHeader title="Channels" />
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.filterRow} contentContainerStyle={styles.filterRowContent}>
        {CHANNEL_FILTERS.map((f) => (
          <TouchableOpacity
            key={f.id}
            style={[styles.filterChip, channelFilter === f.id && styles.filterChipActive]}
            onPress={() => setChannelFilter(f.id)}
            activeOpacity={0.8}
          >
            <Ionicons name={f.icon as any} size={14} color={channelFilter === f.id ? (colors.text.onPrimary as string) : (colors.text.secondary as string)} />
            <Text style={[styles.filterChipText, channelFilter === f.id && styles.filterChipTextActive]}>{f.label}</Text>
          </TouchableOpacity>
        ))}
      </ScrollView>

      {channelFilter === 'all' ? (
        <View style={styles.kpiRow}>
          <KpiTile index={0} minWidth={columns >= 4 ? 150 : undefined} icon="logo-whatsapp" label="WhatsApp" value={stats?.channels.whatsapp.active ?? '—'} />
          <KpiTile index={1} minWidth={columns >= 4 ? 150 : undefined} icon="logo-facebook" label="Facebook" value={stats?.channels.facebookMessenger.active ?? '—'} />
          <KpiTile index={2} minWidth={columns >= 4 ? 150 : undefined} icon="logo-twitter" label="X" value={stats?.channels.xTwitter.active ?? '—'} />
        </View>
      ) : (
        filteredChannelStat && (
          <View style={styles.kpiRow}>
            <KpiTile index={0} minWidth={kpiTileWidth} icon="checkmark-circle-outline" label="Active" value={filteredChannelStat.active} />
            <KpiTile index={1} minWidth={kpiTileWidth} icon="apps-outline" label="Total" value={filteredChannelStat.total} />
            <KpiTile index={2} minWidth={kpiTileWidth} icon="warning-outline" label="Error" value={filteredChannelStat.error} />
          </View>
        )
      )}

      <SectionHeader
        title="Flows & assignments"
        pulsing={
          (flowSessions?.items?.length ?? 0) > 0 ||
          (assignmentStats?.byAgent?.some((a) => a.overdue > 0) ?? false)
        }
      />
      <View style={[styles.row, columns < 2 && styles.rowStacked]}>
        <FlowSessionsCard sessions={flowSessions?.items ?? []} total={flowSessions?.total ?? 0} onViewAll={onFlowsViewAll} />
        <AssignmentBreakdownCard byAgent={assignmentStats?.byAgent ?? []} onViewAll={onAssignmentsViewAll} />
      </View>

      <SectionHeader title="Team" />
      <UsersTeamCard users={users} onViewAll={onTeamViewAll} onSettings={onTeamSettings} />

      <SectionHeader title="Template messages" />
      <TemplateMessagesCard templates={templates} onViewAll={onTemplatesViewAll} onSettings={onTemplatesSettings} />

      <SectionHeader title="Recent campaigns" />
      {campaigns.length === 0 ? (
        <View style={styles.chartCard}>
          <Text style={styles.emptyText}>No campaigns sent yet</Text>
        </View>
      ) : (
        <View style={styles.campaignList}>
          {campaigns.slice(0, 5).map((c) => {
            const statusCfg = CAMPAIGN_STATUS_CONFIG[c.status] ?? CAMPAIGN_STATUS_CONFIG.pending;
            return (
              <View key={c.id} style={styles.campaignRow}>
                <View style={[styles.campaignStatusDot, { backgroundColor: statusCfg.color }]} />
                <View style={styles.campaignInfo}>
                  <Text style={styles.campaignName} numberOfLines={1}>{c.name}</Text>
                  <Text style={styles.campaignMeta}>{c.sent} sent · {c.delivered} delivered · {c.read} read</Text>
                </View>
                <View style={[styles.campaignStatusPill, { backgroundColor: `${statusCfg.color}20` }]}>
                  <Text style={[styles.campaignStatusText, { color: statusCfg.color }]}>{statusCfg.label}</Text>
                </View>
              </View>
            );
          })}
        </View>
      )}
    </ScrollView>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    padding: designTokens.spacing.md,
    gap: designTokens.spacing.md,
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: designTokens.spacing.xs,
    backgroundColor: `${String(colors.status.error)}15`,
    borderRadius: designTokens.borderRadius.md,
    padding: designTokens.spacing.sm,
  },
  errorText: {
    fontSize: typography.fontSize.sm,
    color: colors.status.error,
    fontFamily: typography.fontFamily.medium,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: designTokens.spacing.xs,
  },
  sectionAccentBar: {
    width: 3,
    height: 16,
    borderRadius: 2,
    backgroundColor: colors.brand.primary,
  },
  sectionTitle: {
    fontSize: typography.fontSize.lg,
    fontFamily: typography.fontFamily.bold,
    color: colors.text.primary,
  },
  heroCard: {
    flex: 2,
    minWidth: 180,
    minHeight: 88,
    overflow: 'hidden',
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.lg,
    borderWidth: 1,
    borderColor: colors.border,
    paddingVertical: designTokens.spacing.sm,
    paddingHorizontal: designTokens.spacing.md,
    justifyContent: 'flex-start',
    gap: 2,
    ...designTokens.shadows.lg,
  },
  heroGradient: {
    ...StyleSheet.absoluteFillObject,
    opacity: 0.12,
  },
  heroValue: {
    fontSize: 34,
    fontFamily: typography.fontFamily.bold,
    color: colors.text.primary,
  },
  heroLabel: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  kpiRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: designTokens.spacing.sm,
  },
  row: {
    flexDirection: 'row',
    gap: designTokens.spacing.sm,
  },
  rowStacked: {
    flexDirection: 'column',
  },
  chartCard: {
    flex: 1,
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.lg,
    borderWidth: 1,
    borderColor: colors.border,
    padding: designTokens.spacing.md,
    ...designTokens.shadows.md,
  },
  cardTitle: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.primary,
    marginBottom: designTokens.spacing.sm,
  },
  filterRow: {
    marginTop: -designTokens.spacing.xs,
  },
  filterRowContent: {
    gap: designTokens.spacing.xs,
    paddingBottom: designTokens.spacing.xs,
  },
  filterChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
    paddingHorizontal: designTokens.spacing.sm,
    paddingVertical: designTokens.spacing.xs,
    borderRadius: designTokens.borderRadius.full,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.background.surface,
  },
  filterChipActive: {
    backgroundColor: colors.brand.primary,
    borderColor: colors.brand.primary,
  },
  filterChipText: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.medium,
    color: colors.text.secondary,
  },
  filterChipTextActive: {
    color: colors.text.onPrimary,
  },
  campaignList: {
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.lg,
    borderWidth: 1,
    borderColor: colors.border,
    ...designTokens.shadows.sm,
  },
  campaignRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: designTokens.spacing.sm,
    padding: designTokens.spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  campaignStatusDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  campaignInfo: {
    flex: 1,
  },
  campaignName: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.primary,
  },
  campaignMeta: {
    fontSize: typography.fontSize.xs,
    color: colors.text.secondary,
  },
  campaignStatusPill: {
    paddingHorizontal: designTokens.spacing.sm,
    paddingVertical: 2,
    borderRadius: designTokens.borderRadius.full,
  },
  campaignStatusText: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.semibold,
  },
  emptyText: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
    textAlign: 'center',
    paddingVertical: designTokens.spacing.md,
  },
});
